import React, { Fragment, useMemo, useRef } from "react";
import {
  useTable,
  useFilters,
  useExpanded,
  useRowSelect,
  useSortBy,
  usePagination,
  useRowState
} from "react-table";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Typography } from "@mui/material";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import { useEffect } from "react";
import { IndeterminateCheckbox, SortCell } from "./Cells";
import { useTranslation } from "react-i18next";
import CircularProgressOverlay from "../Table/CircularProgressOverlay";
import { PropTypes } from "prop-types";
import Pagination from "./Pagination";
import DroppableTableBody from "./DroppableTableBody";
import BodyRow from "./BodyRow";

const useStyles = makeStyles(theme => ({
  headerCell: {
    color: "white",
    fontSize: "0.875rem",
    position: "sticky",
    verticalAlign: "center",
    textAlign: "center",
    zIndex: 2,
    top: "0",
    background: theme.gradient,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    padding: theme.spacing(0, 1.5),
    height: theme.spacing(6)
  },
  filter: {
    zIndex: 2,
    top: theme.spacing(6),
    position: "sticky",
    background: "white",
    padding: theme.spacing(0),
    border: "none",
    "&:after": {
      content: `''`,
      position: "absolute",
      left: 0,
      bottom: 0,
      width: "100%",
      borderBottom: `solid 1px ${theme.palette.divider}`
    }
  },
  tableContainer: {
    maxHeight: ({ maxHeight }) => maxHeight,
    minHeight: 0
  },
  selectAll: {
    color: "white"
  },
  checkbox: {
    padding: 0
  },
  rowClass: {
    cursor: ({ isClickable }) => (isClickable ? "pointer" : "default")
  },
  rowExpanded: {
    backgroundColor: theme.palette.action.hover,
    "& > td": {
      borderBottom: "none"
    }
  },
  expansionPanel: {
    backgroundColor: theme.palette.action.hover
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    zIndex: 11
  },
  disabled: {
    color: theme.palette.text.disabled
  },
  isDragging: {
    background: "rgb(235,235,235)",
    "& td": {
      //display: "inline-block"
    }
  }
}));

const SelectAllCheckbox = ({
  toggleAllRowSelectedProps,
  rowSelectionDisabled,
  rows,
  toggleRowsSelected,
  numSelectedRows
}) => {
  const classes = useStyles();

  // Gets the number of selected rows (meaning that the checkbox in the row is not disabled)
  // in order to determine whether or not the select all checkbox should be checked or not
  const numSelectableRows = useMemo(() => {
    if (!rowSelectionDisabled) {
      return rows.length;
    } else {
      return rows && rows.filter(row => !rowSelectionDisabled(row)).length;
    }
  }, [rows]);

  const checked =
    rows?.length > 0 &&
    numSelectableRows === numSelectedRows &&
    numSelectableRows > 0;

  const handleSelectAll = ({ currentTarget: { checked } }) => {
    const rowIdsToToggle = rows.reduce((rowIds, row) => {
      if (!rowSelectionDisabled || !rowSelectionDisabled(row)) {
        return [row.id, ...rowIds];
      } else {
        return rowIds;
      }
    }, []);

    toggleRowsSelected(rowIdsToToggle, checked);
  };

  return (
    <IndeterminateCheckbox
      {...toggleAllRowSelectedProps}
      checked={checked}
      onChange={handleSelectAll}
      classes={{ root: `${classes.selectAll} ${classes.checkbox}` }}
      iconClasses={{ root: classes.selectAll }}
    />
  );
};

const Table = props => {
  const {
    columns,
    data,
    onStateChange,
    updateSelection,
    onCheckRow,
    showRowSelection,
    showSelectAllCheckbox,
    detailPanel,
    showCheckbox,
    disableMainCheckBox,
    manualSortBy,
    manualFilters,
    loading,
    onRowClick,
    disableRowClick,
    cellSize,
    paginated,
    manualPagination,
    rowsPerPageOptions,
    paginationState: {
      setPage,
      setFetchSize,
      initialPage,
      pageSize: manualInitialPageSize,
      totalCount: manualTotalCount,
      page: manualPageNumber
    },
    getRowClass,
    initialRowState,
    onScroll,
    rowSelectionDisabled,
    getRowProps,
    maxHeight,
    initialSortBy,
    controlledSelectedRows,
    tableRef: tableContainerRef,
    getRowTooltipProps,
    updateControlledSelection,
    autoResetSelectedRows,
    onMoveRow,
    ...tableProps
  } = props;
  const { t } = useTranslation();
  const tableRef = tableContainerRef || useRef();

  const totalCount = manualPagination ? manualTotalCount : data.length;
  const initialPageSize = manualPagination
    ? manualInitialPageSize
    : rowsPerPageOptions[0];
  const controlledPageCount = Math.ceil(totalCount / initialPageSize);

  const classes = useStyles({
    isClickable: !!detailPanel || !!onRowClick,
    maxHeight
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state,
    visibleColumns,
    toggleRowExpanded,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    selectedFlatRows,
    setSortBy,
    state: { pageSize, pageIndex, selectedRowIds }
  } = useTable(
    {
      columns,
      data,
      manualFilters: manualFilters,
      manualSortBy: manualSortBy,
      autoResetSelectedRows,
      autoResetExpanded: false,
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetFilters: false,
      getRowId: row => row.id,
      useControlledState: state =>
        useMemo(
          () => ({
            ...state,
            selectedRowIds: controlledSelectedRows || state.selectedRowIds
          }),
          [state, controlledSelectedRows]
        ),
      manualPagination,
      initialState: {
        pageSize: initialPageSize,
        pageIndex: initialPage - 1,
        rowState: initialRowState,
        sortBy: initialSortBy || []
      },
      pageCount: controlledPageCount,
      rowSelectionDisabled,
      onCheckRow,
      updateControlledSelection,
      ...tableProps
    },
    useRowState,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowState,
    useRowSelect,
    hooks => {
      if (!!updateSelection || showRowSelection) {
        hooks.visibleColumns.push(columns => [
          {
            id: "selection",
            Header: ({
              getToggleAllRowsSelectedProps,
              toggleRowSelected,
              rows,
              selectedFlatRows,
              rowSelectionDisabled,
              updateControlledSelection
            }) => {
              const {
                onChange,
                ...otherToggleAllRowsSelectedProps
              } = getToggleAllRowsSelectedProps();

              const toggleRows = (rows, checked) =>
                rows.forEach(rowId => toggleRowSelected(rowId, checked)); // For uncontrolled row selection

              const handleToggleRowsSelected = updateControlledSelection
                ? updateControlledSelection
                : toggleRows;

              return (
                <>
                  {!disableMainCheckBox && (
                    <SelectAllCheckbox
                      toggleAllRowSelectedProps={
                        otherToggleAllRowsSelectedProps
                      }
                      rowSelectionDisabled={rowSelectionDisabled}
                      rows={rows}
                      toggleRowsSelected={handleToggleRowsSelected}
                      numSelectedRows={selectedFlatRows?.length}
                    />
                  )}
                </>
              );
            },
            Cell: ({
              row,
              rowSelectionDisabled,
              onCheckRow,
              updateControlledSelection
            }) => {
              if (showCheckbox && !showCheckbox(row.original)) return <></>;
              const {
                onChange,
                ...toggleRowSelectedProps
              } = row.getToggleRowSelectedProps();

              const handleChange = event => {
                if (updateControlledSelection) {
                  updateControlledSelection([row.id], event.target.checked);
                } else {
                  onChange(event); // For uncontrolled row selection
                }
              };
              return (
                <>
                  <IndeterminateCheckbox
                    classes={{ root: classes.checkbox }}
                    onCheckRow={checked =>
                      onCheckRow && onCheckRow(row, checked)
                    }
                    {...toggleRowSelectedProps}
                    onChange={handleChange}
                    disabled={
                      !!rowSelectionDisabled && rowSelectionDisabled(row)
                    }
                  />
                </>
              );
            },
            style: {
              width: 46,
              maxWidth: 46
            }
          },
          ...columns
        ]);
      }
    }
  );

  // Replace filter ID with filterName property (if provided) in column
  const updateFilterKeys = state => ({
    ...state,
    filters: state.filters.map(filter => {
      const { filterName } = columns.find(({ id }) => id === filter.id);
      return !!filterName ? { id: filterName, value: filter.value } : filter;
    })
  });

  const selectedRowsRef = useRef(selectedRowIds);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({ rowState: state.rowState });
    }
  }, [state.rowState]);

  useEffect(() => {
    if (onStateChange) {
      onStateChange({ selectedRowIds });
    }
  }, [selectedRowIds]);

  useEffect(() => {
    gotoPage(0);
    if (onStateChange) {
      const newState = updateFilterKeys(state);
      onStateChange(newState);
    }
  }, [state.filters, state.sortBy]);

  useEffect(() => {
    if (updateSelection && selectedRowsRef.current !== selectedRowIds) {
      updateSelection(Object.keys(selectedRowIds), selectedFlatRows);
    }
    selectedRowsRef.current = selectedRowIds;
  }, [selectedRowIds, selectedFlatRows, updateSelection]);

  useEffect(() => {
    if (manualPagination) {
      gotoPage(manualPageNumber - 1);
    }
  }, [manualPageNumber]);

  const handleTableCellClick = (row, cell) => {
    if (disableRowClick) return;
    if (cell.column.id !== "selection" && detailPanel) {
      toggleRowExpanded(row.id);
    }
    if (cell.column.id !== "selection" && onRowClick) {
      onRowClick(row, cell);
    }
  };

  const handleGoToPage = newPage => {
    if (tableRef && tableRef.current) {
      tableRef.current.scrollTo(0, 0);
    }
    gotoPage(newPage);
    if (manualPagination) {
      setPage(newPage + 1);
    }
  };

  const handleChangePageSize = newPageSize => {
    if (manualPagination) {
      const newPageIndex = Math.floor((pageSize * pageIndex) / newPageSize);
      setFetchSize(newPageSize);
      // Update the current page based on the new page size
      setPage(newPageIndex + 1);
    }

    setPageSize(newPageSize);
  };

  const handleGoToNextPage = () => {
    if (manualPagination) {
      setPage(pageIndex + 2); // Add 1 to go to the next page plus 1 because react-table is zero indexed
    }
    nextPage();
  };

  const handleGoToPreviousPage = () => {
    if (manualPagination) {
      setPage(pageIndex);
    }
    previousPage();
  };

  const tableRows = paginated && !manualPagination ? page : rows;
  return (
    <Box
      position="relative"
      minHeight={0}
      display="flex"
      flexDirection="column"
    >
      <TableContainer className={classes.tableContainer} ref={tableRef}>
        <MuiTable {...getTableProps()} aria-label="sticky table">
          <TableHead>
            {headerGroups.map((headerGroup, index) => (
              <Fragment key={index}>
                <TableRow
                  className={classes.headerRow}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map(column => (
                    <TableCell
                      className={classes.headerCell}
                      {...column.getHeaderProps()}
                      style={column.style}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ display: "flex", alignItems: "center" }}
                        noWrap
                      >
                        {column.id !== "selection" || !!showSelectAllCheckbox
                          ? column.render("Header")
                          : null}
                        <Box display="flex" alignItems="center" ml={1}>
                          {column.canSort && (
                            <SortCell setSortBy={setSortBy} column={column} />
                          )}
                        </Box>
                      </Typography>
                    </TableCell>
                  ))}
                  {onMoveRow && <TableCell className={classes.headerCell} />}
                </TableRow>
                <TableRow>
                  {headerGroup.headers.map(column => (
                    <TableCell className={classes.filter} key={column.id}>
                      {column.Filter ? column.render("Filter") : <></>}
                    </TableCell>
                  ))}
                </TableRow>
              </Fragment>
            ))}
          </TableHead>
          <TableBody
            {...getTableBodyProps()}
            component={onMoveRow ? DroppableTableBody(onMoveRow) : "tbody"}
          >
            {data && data.length > 0 ? (
              tableRows.map((row, index) => {
                prepareRow(row);
                return (
                  <BodyRow
                    index={index}
                    key={row.id}
                    row={row}
                    classes={classes}
                    getRowTooltipProps={getRowTooltipProps}
                    getRowClass={getRowClass}
                    rowSelectionDisabled={rowSelectionDisabled}
                    onRowClick={onRowClick}
                    getRowProps={getRowProps}
                    onTableCellClick={handleTableCellClick}
                    detailPanel={detailPanel}
                    cellSize={cellSize}
                    visibleColumns={visibleColumns}
                    moveRow={onMoveRow}
                  />
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumns.length}>
                  <Typography variant="body2" sx={{ textAlign: "center" }}>
                    {t("table.noRecordsMsg")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {paginated && (
        <Pagination
          gotoPage={handleGoToPage}
          previousPage={handleGoToPreviousPage}
          nextPage={handleGoToNextPage}
          pageCount={pageCount}
          pageIndex={pageIndex + 1}
          initialPage={initialPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          pageOptions={pageOptions}
          pageSize={pageSize}
          setPageSize={handleChangePageSize}
          totalCount={totalCount}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
      {loading && (
        <div className={classes.loadingOverlay}>
          <CircularProgressOverlay />
        </div>
      )}
    </Box>
  );
};

const MemoizedTable = props => {
  return useMemo(() => {
    return <Table {...props} />;
  }, [
    props.data,
    props.columns,
    props.loading,
    props.paginationState.totalCount,
    props.paginationState.page,
    props.onStateChange,
    props.rowSelectionDisabled
  ]);
};

export default MemoizedTable;

MemoizedTable.propTypes = {
  cellSize: PropTypes.oneOf(["small", "medium"]),
  getRowProps: PropTypes.func,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Shows checkboxe column if true
   */
  showRowSelection: PropTypes.bool,
  /**
   * Used to control which rows are selected in the table
   */
  controlledSelectedRows: PropTypes.object,
  getRowTooltipProps: PropTypes.func
};

MemoizedTable.defaultProps = {
  disableRowClick: false,
  manualSortBy: true,
  manualFilters: true,
  cellSize: "small",
  paginated: false,
  paginationState: {
    setPage: () => {},
    setFetchSize: () => {},
    initialPage: 1,
    initialPageSize: 0,
    totalCount: 0
  },
  initialRowState: {},
  rowsPerPageOptions: [50, 100, 200],
  manualPagination: true,
  getRowProps: () => ({}),
  autoResetRowState: false,
  autoResetSelectedRows: false,
  maxHeight: 600
};
